import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AutoComplete, type AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import FormSelect from './form-select';
import { pipelinesService } from '../../services/pipelines.service';

/**
 * Expected sources, chosen from documents that actually exist.
 *
 * This used to be a free-text box, one file name per line. A typo there is
 * silent: `bns.pdf ` with a trailing space, or `BNS.pdf`, never matches what the
 * retriever returns, so CONTEXTUAL_RECALL and CONTEXT_PRECISION score the case
 * as a miss and the run reads as a retrieval regression that never happened.
 *
 * Documents belong to a pipeline, so one is chosen first and its documents are
 * searched on the server — the Tallman corpus has 15,402 of them, far too many
 * to load into a dropdown.
 *
 * The value is the document's FILE NAME, because that is what a retrieved chunk
 * reports as its source and what the grader compares against. A reference
 * saved earlier that no picker would produce today (`bns.pdf#p12`, pinned to a
 * page) is kept and shown as-is; it is still a valid expectation.
 */

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  error?: string;
}

const SourcePicker = ({ value, onChange, error }: Props) => {
  const [pipelineId, setPipelineId] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  const { data: pipelinesResp, isLoading: pipelinesLoading } = useQuery({
    queryKey: ['pipelines', 'picker'],
    queryFn: () => pipelinesService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const pipelineOptions = (pipelinesResp?.data || []).map((p: { _id: string; pipelineCode?: string; name?: string }) => ({
    value: p._id,
    label: p.name && p.pipelineCode ? `${p.name} (${p.pipelineCode})` : p.pipelineCode || p.name || p._id,
  }));

  const search = async (e: AutoCompleteCompleteEvent) => {
    if (!pipelineId) { setSuggestions([]); return; }
    try {
      setSearchError(null);
      const res = await pipelinesService.listSources(pipelineId, {
        search: e.query.trim() || undefined, page: 1, page_size: 20,
      });
      const names = ((res?.data || []) as { fileName?: string | null; title?: string | null }[])
        .map((s) => s.fileName || s.title || '')
        .filter((n) => n && !value.includes(n));
      setSuggestions([...new Set(names)]);
    } catch {
      setSuggestions([]);
      setSearchError('Could not search this pipeline’s documents.');
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="SourcePicker">
      <FormSelect
        label="Documents from"
        name="sourcePipeline"
        value={pipelineId}
        options={pipelineOptions}
        onChange={(e: { target?: { value: string }; value?: string }) => setPipelineId(e?.target?.value ?? e?.value ?? '')}
        showClear
        filter
        disabled={pipelinesLoading}
        helperText="The pipeline whose documents this answer should come from"
      />
      <div className="flex flex-col gap-1">
        <label htmlFor="expectedSourcesInput" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Expected sources
        </label>
        <AutoComplete
          inputId="expectedSourcesInput"
          multiple
          value={value}
          suggestions={suggestions}
          completeMethod={search}
          onChange={(e) => onChange((e.value as string[]) || [])}
          placeholder={pipelineId ? 'Type to search document names…' : 'Choose a pipeline first'}
          disabled={!pipelineId && value.length === 0}
          // Free typing is exactly the failure this replaces: only a picked
          // suggestion becomes a value.
          forceSelection
          dropdown
          className="w-full"
          inputClassName="w-full"
          pt={{ container: { className: 'w-full flex-wrap' } }}
          data-testid="SourcePicker.Input"
        />
        <span className={`text-xs ${error || searchError ? 'text-rose-600 dark:text-rose-400' : 'text-ink-muted'}`}>
          {error || searchError
            || 'Scored by CONTEXTUAL_RECALL, CONTEXT_PRECISION and CITATION_CORRECTNESS. Leave empty if none should be cited.'}
        </span>
      </div>
    </div>
  );
};

export default SourcePicker;
